import React, { useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Chart from 'chart.js/auto';
import { getInsightBySlug } from '../data/insights';

function formatDate(dateString) {
  if (!dateString) return 'XX';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatReadTime(readTimeMinutes) {
  if (typeof readTimeMinutes === 'number') return `${readTimeMinutes} min read`;
  return `${readTimeMinutes || 'XX'} min read`;
}

function InsightArticle() {
  const { slug } = useParams();
  const story = getInsightBySlug(slug);
  const articleRef = useRef(null);
  const articleUrl = `https://www.clariti.ai/insights/${slug}`;
  const articleImage = story?.image || 'https://www.clariti.ai/images/logos/logo_brain_white_bg.png';
  const articleSchema = story
    ? {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: story.title,
        description: story.excerpt,
        image: [articleImage],
        datePublished: story.publishedDate,
        author: {
          '@type': 'Organization',
          name: story.author || 'Clariti AI',
        },
        publisher: {
          '@type': 'Organization',
          name: 'Clariti AI',
          logo: {
            '@type': 'ImageObject',
            url: 'https://www.clariti.ai/images/logos/logo_brain_white_bg.png',
          },
        },
        mainEntityOfPage: articleUrl,
      }
    : null;

  useEffect(() => {
    if (!story?.hasCharts || !articleRef.current) return undefined;

    const findCanvas = (id) => articleRef.current.querySelector(`#${id}`);
    const charts = [];

    const rateGapCtx = findCanvas('chartRateGap');
    if (rateGapCtx) {
      charts.push(new Chart(rateGapCtx, {
        type: 'bar',
        data: {
          labels: ['High-street avg (easy access)', 'Top easy access rate'],
          datasets: [{ label: 'AER (%)', data: [1.39, 4.5], backgroundColor: '#2563eb' }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: (ctx) => `${Number(ctx.raw).toFixed(2)}%` } },
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: { callback: (v) => `${v}%` },
            },
          },
        },
      }));
    }

    const passCtx = findCanvas('chartPassThrough');
    if (passCtx) {
      charts.push(new Chart(passCtx, {
        type: 'line',
        data: {
          labels: ['Jul 2023', 'Sep 2023', 'Dec 2023', 'Mar 2024', 'Jun 2024'],
          datasets: [
            {
              label: 'Bank Rate (%)',
              data: [5.0, 5.25, 5.25, 5.25, 5.25],
              tension: 0.25,
              borderColor: '#2563eb',
              backgroundColor: '#2563eb',
            },
            {
              label: 'Avg easy access (%)',
              data: [1.66, 1.96, 2.03, 2.12, 2.11],
              tension: 0.25,
              borderColor: '#00b4d8',
              backgroundColor: '#00b4d8',
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { position: 'bottom' } },
          scales: {
            y: { beginAtZero: true, ticks: { callback: (v) => `${v}%` } },
          },
        },
      }));
    }

    const rotCtx = findCanvas('chartRotation');
    if (rotCtx) {
      charts.push(new Chart(rotCtx, {
        type: 'bar',
        data: {
          labels: ['Jul 2023 (implied)', 'Jun 2024'],
          datasets: [
            {
              label: 'Non-interest (£bn)',
              data: [266, 252],
              backgroundColor: '#64748b',
            },
            {
              label: 'Easy access (£bn)',
              data: [909, 909],
              backgroundColor: '#2563eb',
            },
            {
              label: 'Fixed/Notice (£bn)',
              data: [245, 274],
              backgroundColor: '#00b4d8',
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { position: 'bottom' } },
          scales: {
            x: { stacked: true },
            y: {
              stacked: true,
              beginAtZero: true,
              ticks: { callback: (v) => `£${v}bn` },
            },
          },
        },
      }));
    }

    return () => {
      charts.forEach((chart) => chart.destroy());
    };
  }, [story]);

  if (!story) {
    return (
      <div className="page-wrapper">
        <Helmet>
          <title>Story Not Found | Clariti AI Insights</title>
          <meta name="robots" content="noindex,follow" />
          <link rel="canonical" href="https://www.clariti.ai/insights" />
        </Helmet>
        <section className="resources-hero">
          <div className="container">
            <h1>Story not found</h1>
            <p>The story you are looking for does not exist yet.</p>
            <Link to="/insights" className="btn btn-primary" style={{ marginTop: '20px' }}>
              Back to Insights
            </Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      {story && (
        <Helmet>
          <title>{story.title} | Clariti AI Insights</title>
          <meta name="description" content={story.excerpt} />
          <link rel="canonical" href={articleUrl} />
          
          {/* Social Media Tags */}
          <meta property="og:title" content={story.title} />
          <meta property="og:description" content={story.excerpt} />
          <meta property="og:type" content="article" />
          <meta property="og:url" content={articleUrl} />
          <meta property="og:image" content={articleImage} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={story.title} />
          <meta name="twitter:description" content={story.excerpt} />
          <meta name="twitter:image" content={articleImage} />
          {articleSchema && (
            <script type="application/ld+json">
              {JSON.stringify(articleSchema)}
            </script>
          )}
        </Helmet>
      )}
      {!story.useEmbeddedHeader && (
        <section className="insight-article-hero">
          <div className="container insight-article-hero-inner">
            <p className="insight-card-kicker">{story.category}</p>
            <h1>{story.title}</h1>
            <p className="insight-article-meta">
              {formatDate(story.publishedDate)} · {formatReadTime(story.readTimeMinutes)} · {story.author || 'XX'}
            </p>
          </div>
        </section>
      )}

      <section className="insight-article-section">
        <div className="container">
          <article
            ref={articleRef}
            className="insight-article-body"
            dangerouslySetInnerHTML={{ __html: story.bodyHtml }}
          />
          <div className="insight-article-footer">
            <Link to="/insights" className="insight-card-link">
              ← Back to all insights
            </Link>
            <span style={{ margin: '0 8px' }}>·</span>
            <Link to="/products" className="insight-card-link">
              Explore products
            </Link>
            <span style={{ margin: '0 8px' }}>·</span>
            <Link to="/contact" className="insight-card-link">
              Book a demo
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default InsightArticle;

